import Box from "@mui/material/Box";
import { keyframes } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef } from "react";

import {
  BOOT_INDICATOR_FADE_IN,
  BOOT_INDICATOR_FADE_OUT,
  BOOT_INDICATOR_INTENSITY,
  BOOT_INDICATOR_LABELS,
  type BootIndicatorLook,
} from "@/ui/bootIndicatorVisuals";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const breathe = keyframes`
  0%, 100% { opacity: .35; transform: scale(.8); }
  50% { opacity: 1; transform: scale(1.1); }
`;

/** Positions of the three dots inside the 20px mark, as a triangle like the Storyboard loader. */
const DOT_POSITIONS = [
  { left: "7.5px", top: 0 },
  { left: "0.8px", top: "12px" },
  { left: "14.2px", top: "12px" },
];

/**
 * A small three-dot mark that turns slowly while each dot breathes. Stays mounted while hidden so it can fade out.
 *
 * @param look - The look to show, or `undefined` to fade out.
 */
export default function BootDots({ look }: { look: BootIndicatorLook | undefined }) {
  // Keep the last active look while fading out, so the dots keep their colour and position.
  const lastLook = useRef(look);
  useEffect(() => {
    if (look) lastLook.current = look;
  }, [look]);
  const shownLook = look ?? lastLook.current;
  const intensity = BOOT_INDICATOR_INTENSITY[shownLook ?? "background"];
  const label = look ? BOOT_INDICATOR_LABELS[look] : "";
  const running = look !== undefined;
  // Pausing rather than removing the animations holds the mark in place through the fade-out.
  const playState = running ? "running" : "paused";
  const loop = `${intensity.durationSeconds}s`;

  return (
    <Tooltip title={label} placement="right" arrow>
      <Box
        role={running ? "status" : undefined}
        aria-label={running ? label : undefined}
        aria-hidden={!running}
        sx={{
          width: "32px",
          height: "32px",
          display: "grid",
          placeItems: "center",
          opacity: running ? intensity.opacity : 0,
          transition: running ? BOOT_INDICATOR_FADE_IN : BOOT_INDICATOR_FADE_OUT,
          pointerEvents: running ? "auto" : "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "20px",
            height: "20px",
            animation: shownLook
              ? `${spin} ${loop} linear infinite ${intensity.reverse ? "reverse" : "normal"}`
              : "none",
            animationPlayState: playState,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {DOT_POSITIONS.map((position, index) => (
            <Box
              key={position.left}
              sx={{
                position: "absolute",
                ...position,
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                bgcolor: intensity.color,
                boxShadow: `0 0 ${intensity.glowPx / 2}px ${intensity.color}`,
                animation: shownLook ? `${breathe} ${loop} ease-in-out infinite` : "none",
                animationPlayState: playState,
                // Stagger the dots a third of a loop apart so the breath travels around the mark.
                animationDelay: `calc(${loop} * ${-index / 3})`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.9 },
              }}
            />
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
}
